import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Sidebar from "@/components/Sidebar";
import { 
  Package, 
  Menu,
  Search,
  ChevronRight,
  Laptop,
  Tablet,
  Monitor,
  Shield,
  Home,
  Clock,
  X
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  "Business Laptops": Laptop,
  "Tablet Solutions": Tablet,
  "Office Setup": Monitor,
  "Cybersecurity": Shield,
  "Home Office": Home,
  "Desktops": Monitor
};

export default function SolutionsBrowse() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Get category from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("category");
    if (cat) setSelectedCategory(cat);
  }, [location.search]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bundlesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/bundles`, { withCredentials: true }),
        axios.get(`${API}/solutions/categories`, { withCredentials: true })
      ]);
      setBundles(bundlesRes.data.bundles);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBundles = bundles.filter(bundle => {
    const matchesSearch = !searchQuery || 
      bundle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bundle.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || bundle.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
      />

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-white/5 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-heading text-xl font-semibold">Curated Solutions Browse</h1>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6 space-y-8">
          {/* CTA Banner */}
          <div className="bg-gradient-to-r from-[#007AFF] to-[#0055CC] rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="font-heading font-bold text-xl text-foreground mb-2">Ready to configure your IT solution?</h2>
              <p className="text-muted-foreground text-sm">Select devices, add management software, and request a quote in minutes.</p>
            </div>
            <Button
              onClick={() => navigate("/configure")}
              className="bg-white text-[#007AFF] hover:bg-white/90 font-semibold px-6 h-12 whitespace-nowrap"
              data-testid="configure-cta"
            >
              Start Configuring
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>

          {/* Search */}
          <div className="flex gap-4">
            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder='Select or type what you are looking for: example "need Apple Macbooks for 20 engineers"'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-card border-border text-base"
                data-testid="search-input"
              />
            </div>
            <Button 
              className="h-12 px-8 bg-[#007AFF] hover:bg-[#0055CC]"
              data-testid="search-btn"
            >
              Search
            </Button>
          </div>

          {/* Category filter chip - only shown when filtering via URL param */}
          {selectedCategory && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtered by:</span>
              <button
                onClick={() => { setSelectedCategory(null); navigate("/solutions"); }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/20 hover:bg-[#007AFF]/20 transition-colors"
              >
                <span className="text-sm text-[#007AFF]">{selectedCategory}</span>
                <X className="w-3.5 h-3.5 text-[#007AFF]" />
              </button>
            </div>
          )}

          {/* Bundles Grid */}
          <div className="space-y-4">
            <h2 className="font-heading font-semibold text-lg">
              {selectedCategory ? `${selectedCategory} Bundles` : "All Solutions"}
            </h2>

            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-6">
                    <div className="skeleton h-6 w-3/4 rounded mb-4" />
                    <div className="skeleton h-4 w-full rounded mb-2" />
                    <div className="skeleton h-4 w-2/3 rounded mb-4" />
                    <div className="skeleton h-10 w-full rounded" />
                  </div>
                ))}
              </div>
            ) : filteredBundles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No solutions found matching your criteria</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchQuery("");
                  }}
                >
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBundles.map((bundle) => (
                  <div
                    key={bundle.bundle_id}
                    className={`bg-card border rounded-xl p-6 card-glow category-card ${
                      bundle.is_recommended ? "border-[#007AFF]" : "border-border"
                    }`}
                    data-testid={`bundle-card-${bundle.bundle_id}`}
                  >
                    {bundle.is_recommended && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full recommended-badge text-xs font-medium mb-3">
                        Recommended
                      </div>
                    )}

                    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center mb-4">
                      {categoryIcons[bundle.category] ? (
                        React.createElement(categoryIcons[bundle.category], { className: "w-6 h-6 text-muted-foreground" })
                      ) : (
                        <Package className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    
                    <h3 className="font-heading font-semibold text-lg mb-2">{bundle.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {bundle.description}
                    </p>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <Clock className="w-4 h-4" />
                      <span>Lead time: {bundle.lead_time_days} days</span>
                    </div>
                    
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-bold font-heading">
                        ₹{bundle.base_price.toLocaleString()}
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        ₹{bundle.market_price.toLocaleString()}
                      </span>
                    </div>
                    
                    <div className="savings-highlight rounded-lg px-3 py-2 mb-4">
                      <p className="text-sm text-[#00FF88]">
                        Save ₹{(bundle.market_price - bundle.base_price).toLocaleString()} 
                        ({Math.round((1 - bundle.base_price / bundle.market_price) * 100)}%)
                      </p>
                    </div>

                    <Button
                      onClick={() => navigate(`/bundles/${bundle.bundle_id}`)}
                      className="w-full bg-white text-black hover:bg-gray-200"
                      data-testid={`select-bundle-${bundle.bundle_id}`}
                    >
                      Select Bundle
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CTA Button */}
          <div className="flex justify-center pt-4">
            <Button
              size="lg"
              className="bg-[#00FF88] text-black hover:bg-[#00CC6A] btn-hover-scale"
              onClick={() => navigate("/compare")}
              data-testid="select-solution-btn"
            >
              Select a solution
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
}
